import React, { useEffect } from 'react';
import SectionHead from '../components/ui/SectionHead.jsx';
import Sidebar from '../components/ui/Sidebar.jsx';

const CORE_DISCIPLINES = [
  'General medicine and internal medicine',
  'Surgery and surgical specialties (e.g., orthopaedics, neurosurgery, cardiothoracic surgery)',
  'Anesthesiology and pain management',
  'Diagnostic and interventional radiology',
  'Pathology and laboratory medicine',
  'Pediatrics',
  'Obstetrics and Gynaecology',
  'Public health, epidemiology, and preventive medicine',
];

const ARTICLE_TYPES = [
  { icon: '📄', label: 'Original Research Articles' },
  { icon: '🔬', label: 'Review Articles (Systematic Reviews, Meta-analyses, Narrative Reviews)' },
  { icon: '🩺', label: 'Case Reports and Case Series' },
  { icon: '✉️', label: 'Letters to the Editor' },
  { icon: '💬', label: 'Commentaries and Editorials' },
  { icon: '🏥', label: 'Medical and Surgical Innovations' },
  { icon: '⚡', label: 'EDGE (Evidence, Debate, Guidance & Experience)' },
];

const DISCIPLINES = [
  'Clinical Practice',
  'Clinical Trials',
  'Broad Medical Specialities',
  'Epidemiology and Public Health',
  'Health System and Policy',
  'Medical Education',
  'Global Health',
  'Digital Health and Informatics',
  'Biomedical Sciences',
];

const sidebarLinks = [
  { to: '/author-guidelines', label: 'Author Guidelines' },
  { to: '/peer-review-policy', label: 'Peer Review Policy' },
  { to: '/editorial-board', label: 'Editorial Board' },
  { to: '/contact', label: 'Contact Us' },
];

function AimsAndScope() {
  useEffect(() => {
    document.title = 'Aims & Scope — JAMSD';
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">
                Journal Information
              </p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">
                Aims &amp; Scope
              </h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">
                Understanding what JAMSD publishes and the disciplines we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Overview */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8 mb-8">
              <SectionHead title="Journal Overview" />
              <div className="prose prose-slate max-w-none font-sans text-gray-700 leading-relaxed space-y-4">
                <p>
                  The <strong className="font-semibold text-navy">Journal of Applied Medical and Surgical Disciplines (JAMSD, ISSN: XXXX-XXXX)</strong>{' '}
                  is a multidisciplinary, peer-reviewed, bi-yearly journal (January &amp; July) committed to
                  publishing high-quality research and reviews in the broad field of medical and surgical
                  sciences. The journal aims to foster communication and collaboration among clinicians,
                  researchers, and healthcare professionals across various disciplines, including but not
                  limited to:
                </p>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  {CORE_DISCIPLINES.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>
                  The journal welcomes original research articles, review articles, case reports and series,
                  and letters to the editor on topics within its scope. It focuses on clinical and
                  translational research, therapeutic innovations, and advancements in diagnostic techniques,
                  with an emphasis on improving patient outcomes and healthcare delivery.
                </p>
                <p>
                  The scope of the journal encompasses all aspects of medical and surgical disciplines,
                  aiming to provide a platform for the dissemination of knowledge, discussion of challenges,
                  and exchange of ideas among professionals globally. By publishing bi-annually, JAMSD ensures
                  timely dissemination of research findings and clinical insights, contributing to the
                  advancement of medical science and practice.
                </p>
              </div>
            </div>

            {/* EDGE */}
            <div className="bg-amber-tint rounded-lg border-2 border-amber p-6 md:p-8 mb-8">
              {/* <span className="inline-block bg-amber text-white text-xs font-sans font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-3">
                Special Section
              </span> */}
              <h2 className="font-display text-navy text-2xl font-bold mb-3">
                Introducing EDGE: Evidence, Debate, Guidance &amp; Experience
              </h2>
              <div className="font-sans text-amber-tinttext leading-relaxed space-y-3 text-sm md:text-base">
                <p>
                  We&#x2019;re thrilled to unveil <strong>EDGE</strong>, a groundbreaking special section in the{' '}
                  <em>Journal of Applied Medical and Surgical Disciplines</em> — your razor-sharp gateway to
                  transforming research into real-world impact. EDGE stands for <strong>Evidence, Debate,
                  Guidance &amp; Experience</strong>, capturing the essence of multidisciplinary medicine where
                  data drives discourse, insights forge paths, and stories shape futures.
                </p>
                <p>
                  In an era of rapid clinical evolution — from precision surgery to digital therapeutics —
                  EDGE cuts through the noise. This curated space features provocative, bite-sized symposia
                  that blend rigor with relevance, spotlighting:
                </p>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li><strong>Evidence:</strong> Incisive breakdowns of landmark studies and emerging data that redefine protocols.</li>
                  <li><strong>Debate:</strong> Fiery exchanges between thought leaders on pressing dilemmas, like telemedicine ethics or antibiotic stewardship.</li>
                  <li><strong>Guidance:</strong> Streamlined frameworks and checklists to bridge the gap from journal to practice.</li>
                  <li><strong>Experience:</strong> Candid reflections from frontline innovators, unpacking successes and setbacks in global health arenas.</li>
                </ul>
                <p>
                  Designed for the busy practitioner, researcher, or educator, EDGE delivers high-octane
                  content in accessible formats — think 1,500-word debates, infographic guides, and video
                  spotlights — all open-access and optimized for mobile. Our biannual issues anchor EDGE with
                  themed spotlights. Authors across various disciplines are invited to write an article for
                  this section.
                </p>
              </div>
            </div>

            {/* Scope Description */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8 mb-8">
              <SectionHead title="Scope" />
              <p className="font-sans text-gray-700 leading-relaxed mb-4">
                JAMSD is an international, peer-reviewed, open-access e-journal focused on high-quality,
                multidisciplinary research in medical and health sciences. We publish bi-yearly (January and
                July) with content available online immediately upon acceptance.
              </p>
              <p className="font-sans text-gray-700 leading-relaxed">
                The journal emphasizes applied research that has direct relevance to clinical practice, public
                health, and health policy. We encourage submissions from diverse geographical regions and
                welcome international collaboration and perspectives.
              </p>
            </div>

            {/* Accepted Article Types */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8 mb-8">
              <SectionHead title="Accepted Article Types" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ARTICLE_TYPES.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 bg-bg-cool rounded-lg p-4 border border-bg-border"
                  >
                    <span className="text-xl flex-shrink-0" role="img" aria-hidden="true">
                      {item.icon}
                    </span>
                    <p className="font-sans text-gray-700 text-sm leading-relaxed">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Target Disciplines */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8 mb-8">
              <SectionHead title="Target Disciplines" />
              <p className="font-sans text-gray-700 text-sm leading-relaxed mb-5">
                JAMSD publishes research across the following disciplines and their intersections:
              </p>
              <div className="flex flex-wrap gap-3">
                {DISCIPLINES.map((discipline) => (
                  <span
                    key={discipline}
                    className="bg-bg-tag text-navy font-sans text-sm font-medium px-4 py-2 rounded-full border border-bg-border"
                  >
                    {discipline}
                  </span>
                ))}
              </div>
            </div>

            {/* Publication Info */}
            <div className="bg-amber-tint rounded-lg border border-amber p-6">
              <h3 className="font-display text-navy font-bold text-xl mb-3">Publication Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 font-sans text-sm">
                <div>
                  <p className="text-amber-tinttext font-semibold mb-1">Frequency</p>
                  <p className="text-gray-700">Bi-yearly (January and July)</p>
                </div>
                <div>
                  <p className="text-amber-tinttext font-semibold mb-1">Access Model</p>
                  <p className="text-gray-700">Fully Open Access (CC BY 4.0)</p>
                </div>
                <div>
                  <p className="text-amber-tinttext font-semibold mb-1">Article Processing Charges</p>
                  <p className="text-gray-700">None — No APCs</p>
                </div>
                <div>
                  <p className="text-amber-tinttext font-semibold mb-1">Review Process</p>
                  <p className="text-gray-700">Double-blind peer review</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-amber-tinttext font-semibold mb-1">Publication Dates</p>
                  <p className="text-gray-700">January 1 and July 1, 2026</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 xl:w-80 flex-shrink-0">
            <Sidebar
              quickLinks={sidebarLinks}
              showSubmitCta
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default AimsAndScope;
